import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fetchAPI, type CategoryScope } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { invalidateItemPurchasingCosts } from "@/lib/item-purchasing-cost-cache";

export type OrderLocationOption = {
  id: string;
  name: string;
  type?: string | null;
};

export type OrderLocationOptionsResponse = {
  sources: OrderLocationOption[];
  destinations: OrderLocationOption[];
};

export type OperationalLocationOptionsResponse = OrderLocationOptionsResponse;

export function useLocations() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/locations", workspaceId],
    queryFn: () => workspaceId ? api.locations.list(workspaceId) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function useOrderLocationOptions() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery<OrderLocationOptionsResponse>({
    queryKey: ["/api/orders/location-options", workspaceId],
    queryFn: () => workspaceId
      ? fetchAPI("/orders/location-options") as Promise<OrderLocationOptionsResponse>
      : Promise.resolve({ sources: [], destinations: [] }),
    enabled: !!workspaceId,
  });
}

export function useTransferLocationOptions() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery<OperationalLocationOptionsResponse>({
    queryKey: ["/api/transfers/location-options", workspaceId],
    queryFn: () => workspaceId
      ? fetchAPI("/transfers/location-options") as Promise<OperationalLocationOptionsResponse>
      : Promise.resolve({ sources: [], destinations: [] }),
    enabled: !!workspaceId,
  });
}

export function useReturnLocationOptions(returnType: string) {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery<OperationalLocationOptionsResponse>({
    queryKey: ["/api/returns/location-options", workspaceId, returnType],
    queryFn: () => workspaceId
      ? fetchAPI(`/returns/location-options?returnType=${encodeURIComponent(returnType)}`) as Promise<OperationalLocationOptionsResponse>
      : Promise.resolve({ sources: [], destinations: [] }),
    enabled: !!workspaceId && !!returnType,
  });
}

export function useItems(opts?: { activeOnly?: boolean }) {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;
  const activeOnly = opts?.activeOnly ?? false;

  return useQuery({
    queryKey: ["/api/items", workspaceId, activeOnly ? "active" : "all"],
    queryFn: () => workspaceId ? api.items.list(workspaceId, { activeOnly }) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function useItem(itemId: string | undefined) {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/items", itemId, workspaceId],
    queryFn: () => itemId && workspaceId ? api.items.get(itemId) : Promise.resolve(null),
    enabled: !!itemId && !!workspaceId,
  });
}

export function useCategories(scope?: CategoryScope) {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;
  const scopeKey = scope || "all";

  return useQuery({
    // Keep the scope in the cache identity. The same category id can be
    // returned by the general and finished-product taxonomies.
    queryKey: ["/api/categories", workspaceId, scopeKey],
    queryFn: () => workspaceId ? api.categories.list(workspaceId, scope) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function useSubcategories(categoryId?: string, scope?: CategoryScope) {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;
  const scopeKey = scope || "all";

  return useQuery({
    queryKey: ["/api/subcategories", workspaceId, categoryId || "all", scopeKey],
    queryFn: () => workspaceId ? api.subcategories.list(workspaceId, categoryId, scope) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function useUnits() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/units", workspaceId],
    queryFn: () => workspaceId ? api.units.list(workspaceId) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function useVehicles() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/vehicles", workspaceId],
    queryFn: () => workspaceId ? api.vehicles.list(workspaceId) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function useCreateVehicle() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();
  return useMutation({
    mutationFn: (data: any) => api.vehicles.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vehicles"] });
    },
  });
}

export function useUpdateVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.vehicles.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vehicles"] });
    },
  });
}

export function useDeactivateVehicle() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();
  return useMutation({
    mutationFn: (id: string) => {
      if (!currentWorkspace) throw new Error("No workspace selected");
      return api.vehicles.deactivate(id, currentWorkspace.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vehicles"] });
    },
  });
}

export function useBrands() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/brands", workspaceId],
    queryFn: () => workspaceId ? api.brands.list(workspaceId) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function useStock(locationId?: string) {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/stock", workspaceId, locationId],
    queryFn: () => workspaceId ? api.stock.list({ workspaceId, locationId }) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function useOrders(params?: {
  page?: number;
  limit?: number;
  status?: string;
  branchId?: string;
  fromDate?: string;
  toDate?: string;
  forMe?: boolean;
  forApproverId?: string | null;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  itemId?: string;
}) {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;
  const page = params?.page;
  const limit = params?.limit;
  const status = params?.status;
  const branchId = params?.branchId;
  const fromDate = params?.fromDate;
  const toDate = params?.toDate;
  const forMe = params?.forMe;
  const forApproverId = params?.forApproverId ?? undefined;
  const sortBy = params?.sortBy;
  const sortDir = params?.sortDir;
  const itemId = params?.itemId;

  return useQuery({
    queryKey: ["/api/orders", workspaceId, page, limit, status, branchId, fromDate, toDate, forMe, forApproverId, sortBy, sortDir, itemId],
    queryFn: () =>
      workspaceId
        ? api.orders.list({ workspaceId, page, limit, status, branchId, fromDate, toDate, forMe, forApproverId, sortBy, sortDir, itemId })
        : Promise.resolve({ orders: [], total: 0, page: 1, limit: 25 }),
    enabled: !!workspaceId,
  });
}

export function useOrder(orderId: string | undefined) {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/orders", orderId, workspaceId],
    queryFn: () => orderId && workspaceId ? api.orders.get(orderId) : Promise.resolve(null),
    enabled: !!orderId && !!workspaceId,
  });
}

export function useOrderTemplates() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/templates", workspaceId],
    queryFn: () => workspaceId ? api.templates.list(workspaceId) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function usePriceHistory(itemId?: string) {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/price-history", itemId || "all", workspaceId],
    queryFn: () => {
      if (itemId && workspaceId) {
        return api.priceHistory.get(itemId, workspaceId);
      }
      if (workspaceId) {
        return api.priceHistory.list(workspaceId);
      }
      return Promise.resolve([]);
    },
    enabled: !!workspaceId,
  });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.orders.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
    },
  });
}

export function useUpdateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.orders.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
    },
  });
}

export function useCreateItem() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.items.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/items"] });
      if (currentWorkspace?.id) void invalidateItemPurchasingCosts(queryClient, currentWorkspace.id);
    },
  });
}

export function useUpdateItem() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.items.update(id, { ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/items"] });
      if (currentWorkspace?.id) void invalidateItemPurchasingCosts(queryClient, currentWorkspace.id);
    },
  });
}

export function useDeleteItem() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (id: string) => {
      if (!currentWorkspace?.id) {
        return Promise.reject(new Error("No workspace selected"));
      }
      return api.items.delete(id, currentWorkspace.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/items"] });
    },
  });
}

export function useCreateCategory(scope?: CategoryScope) {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.categories.create({
      ...data,
      ...(scope ? { scope } : {}),
      workspaceId: currentWorkspace?.id,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/categories"] });
    },
  });
}

export function useUpdateCategory(scope?: CategoryScope) {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.categories.update(id, {
        ...data,
        ...(scope ? { scope } : {}),
        workspaceId: currentWorkspace?.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/categories"] });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (id: string) => {
      if (!currentWorkspace?.id) {
        return Promise.reject(new Error("No workspace selected"));
      }
      return api.categories.delete(id, currentWorkspace.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/categories"] });
    },
  });
}

export function useCreateSubcategory(scope?: CategoryScope) {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.subcategories.create({
      ...data,
      ...(scope ? { scope } : {}),
      workspaceId: currentWorkspace?.id,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subcategories"] });
    },
  });
}

export function useUpdateSubcategory(scope?: CategoryScope) {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.subcategories.update(id, {
        ...data,
        ...(scope ? { scope } : {}),
        workspaceId: currentWorkspace?.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subcategories"] });
    },
  });
}

export function useDeleteSubcategory() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (id: string) => {
      if (!currentWorkspace?.id) return Promise.reject(new Error("No workspace selected"));
      return api.subcategories.delete(id, currentWorkspace.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subcategories"] });
      queryClient.invalidateQueries({ queryKey: ["/api/items"] });
    },
  });
}

export function useCreateUnit() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.units.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/units"] });
    },
  });
}

export function useUpdateUnit() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.units.update(id, { ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/units"] });
    },
  });
}

export function useDeleteUnit() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (id: string) => {
      if (!currentWorkspace?.id) {
        return Promise.reject(new Error("No workspace selected"));
      }
      return api.units.delete(id, currentWorkspace.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/units"] });
    },
  });
}

export function useCreateBrand() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.brands.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands"] });
    },
  });
}

export function useUpdateBrand() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.brands.update(id, { ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands"] });
    },
  });
}

export function useDeleteBrand() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (id: string) => {
      if (!currentWorkspace?.id) {
        return Promise.reject(new Error("No workspace selected"));
      }
      return api.brands.delete(id, currentWorkspace.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands"] });
    },
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.locations.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/orders/location-options"] });
      queryClient.invalidateQueries({ queryKey: ["/api/transfers/location-options"] });
      queryClient.invalidateQueries({ queryKey: ["/api/returns/location-options"] });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.locations.update(id, { ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/orders/location-options"] });
      queryClient.invalidateQueries({ queryKey: ["/api/transfers/location-options"] });
      queryClient.invalidateQueries({ queryKey: ["/api/returns/location-options"] });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (id: string) => {
      if (!currentWorkspace?.id) {
        return Promise.reject(new Error("No workspace selected"));
      }
      return api.locations.delete(id, currentWorkspace.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/orders/location-options"] });
      queryClient.invalidateQueries({ queryKey: ["/api/transfers/location-options"] });
      queryClient.invalidateQueries({ queryKey: ["/api/returns/location-options"] });
    },
  });
}

export function useUpsertStock() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.stock.upsert({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/stock"] });
    },
  });
}

export function useCreatePriceHistory() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.priceHistory.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-history", variables.itemId] });
      queryClient.invalidateQueries({ queryKey: ["/api/items"] });
    },
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.templates.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/templates"] });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => {
      if (!currentWorkspace?.id) {
        return Promise.reject(new Error("No workspace selected"));
      }
      return api.templates.update(id, { ...data, workspaceId: currentWorkspace.id }, currentWorkspace.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/templates"] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (id: string) => {
      if (!currentWorkspace?.id) {
        return Promise.reject(new Error("No workspace selected"));
      }
      return api.templates.delete(id, currentWorkspace.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/templates"] });
    },
  });
}

export function usePriceChangeReasons() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: ["/api/price-change-reasons", workspaceId],
    queryFn: () => workspaceId ? api.priceChangeReasons.list(workspaceId) : Promise.resolve([]),
    enabled: !!workspaceId,
  });
}

export function useCreatePriceChangeReason() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.priceChangeReasons.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-change-reasons"] });
    },
  });
}

export function useUpdatePriceChangeReason() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.priceChangeReasons.update(id, { ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-change-reasons"] });
    },
  });
}

export function useDeletePriceChangeReason() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (id: string) => api.priceChangeReasons.delete(id, currentWorkspace?.id || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-change-reasons"] });
    },
  });
}

export function useDeliveryZones() {
  const { currentWorkspace } = useAuth();
  return useQuery({
    queryKey: ["/api/delivery-zones", currentWorkspace?.id],
    queryFn: () => api.deliveryZones.list(currentWorkspace?.id || ""),
    enabled: !!currentWorkspace,
  });
}

export function useCreateDeliveryZone() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.deliveryZones.create({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/delivery-zones"] });
    },
  });
}

export function useUpdateDeliveryZone() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.deliveryZones.update(id, { ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/delivery-zones"] });
    },
  });
}

export function useDeleteDeliveryZone() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (id: string) => api.deliveryZones.delete(id, currentWorkspace?.id || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/delivery-zones"] });
    },
  });
}

export function useWarehouseZoneLeadTimes(warehouseId?: string) {
  const { currentWorkspace } = useAuth();
  return useQuery({
    queryKey: ["/api/warehouse-zone-lead-times", currentWorkspace?.id, warehouseId],
    queryFn: () => api.warehouseZoneLeadTimes.list(currentWorkspace?.id || "", warehouseId),
    enabled: !!currentWorkspace,
  });
}

export function useUpsertWarehouseZoneLeadTime() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: any) => api.warehouseZoneLeadTimes.upsert({ ...data, workspaceId: currentWorkspace?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/warehouse-zone-lead-times"] });
    },
  });
}

export function useDeleteWarehouseZoneLeadTime() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useAuth();

  return useMutation({
    mutationFn: (data: { warehouseId: string; zoneId: string }) => 
      api.warehouseZoneLeadTimes.delete({ ...data, workspaceId: currentWorkspace?.id || "" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/warehouse-zone-lead-times"] });
    },
  });
}
